/**
 * Comparison Service client — API methods for comparing contract document
 * versions, listing past comparisons, and retrieving individual summaries.
 *
 * Requirements: 21.1, 21.3, 21.4, 21.5, 21.6
 */

import { apiClient } from '@/services/apiClient';

// ─── Types ─────────────────────────────────────────────────────────────

/** A single change within a category. */
export interface CategoryChange {
  type: 'addition' | 'removal' | 'modification';
  description: string;
  significance: 'High' | 'Medium' | 'Low';
  originalText?: string;
  revisedText?: string;
}

/** A group of changes categorized by clause type. */
export interface ChangeCategory {
  category: string;
  changes: CategoryChange[];
}

/** A stored change summary record. */
export interface ChangeSummary {
  summaryId: number;
  contractId: number;
  version1EntryId: string;
  version2EntryId: string;
  categories: ChangeCategory[];
  totalAdditions: number;
  totalRemovals: number;
  totalModifications: number;
  createdAt: string;
  createdBy: string;
}

/** Line-level diff addition. */
export interface DiffAddition {
  lineNumber: number;
  content: string;
}

/** Line-level diff removal. */
export interface DiffRemoval {
  lineNumber: number;
  content: string;
}

/** Line-level diff modification. */
export interface DiffModification {
  lineNumber: number;
  original: string;
  revised: string;
}

/** Result of a line-by-line text diff. */
export interface TextDiffResult {
  additions: DiffAddition[];
  removals: DiffRemoval[];
  modifications: DiffModification[];
  totalChanges: number;
}

/** Full comparison result returned by the compare endpoint. */
export interface ComparisonResult {
  diff: TextDiffResult;
  changeSummary: ChangeSummary;
  disclaimer: string;
}

// ─── Service ───────────────────────────────────────────────────────────

export const comparisonService = {
  /**
   * Compare two contract document versions.
   * POST /api/contracts/:id/compare
   */
  compareVersions(
    contractId: number,
    version1EntryId: string,
    version2EntryId: string,
  ): Promise<ComparisonResult> {
    return apiClient.post(`contracts/${contractId}/compare`, {
      version1EntryId,
      version2EntryId,
    });
  },

  /**
   * List all past comparisons for a contract.
   * GET /api/contracts/:id/comparisons
   */
  listComparisons(contractId: number): Promise<ChangeSummary[]> {
    return apiClient.get(`contracts/${contractId}/comparisons`);
  },

  /**
   * Get a specific change summary by ID.
   * GET /api/comparisons/:summaryId
   */
  getComparison(summaryId: number): Promise<ChangeSummary> {
    return apiClient.get(`comparisons/${summaryId}`);
  },
};
